import React from 'react';

// Mobile patients screen content from the design (§05): title, search, filter chips, alphabetically grouped rows.
const mono = "'JetBrains Mono',monospace";
export interface MobileRowVM { id: number; name: string; initials: string; sub: string; alert?: boolean; badge?: { text: string; bg: string; color: string }; plain?: string }

const ACTION_W = 76;

// Row that slides left on touch to reveal its actions (WhatsApp / Schedule); idle state is visually identical to a plain row.
const SwipeRow: React.FC<{ actions: { label: string; onClick: () => void; bg: string }[]; children: React.ReactNode }> = ({ actions, children }) => {
  const [dx, setDx] = React.useState(0);
  const start = React.useRef<{ x: number; y: number; base: number } | null>(null);
  const max = actions.length * ACTION_W;
  if (!actions.length) return <>{children}</>;
  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, display: 'flex' }}>
        {actions.map(a => <button key={a.label} onClick={() => { setDx(0); a.onClick(); }} style={{ width: ACTION_W, border: 0, background: a.bg, color: 'var(--surface,#fff)', font: '500 12px Inter,sans-serif', cursor: 'pointer', minHeight: 0, padding: 0 }}>{a.label}</button>)}
      </div>
      <div
        onTouchStart={e => { start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, base: dx }; }}
        onTouchMove={e => {
          const s = start.current; if (!s) return;
          const mx = e.touches[0].clientX - s.x, my = e.touches[0].clientY - s.y;
          if (Math.abs(my) > Math.abs(mx)) return; // vertical scroll wins
          setDx(Math.max(-max, Math.min(0, s.base + mx)));
        }}
        onTouchEnd={() => { setDx(d => (d < -max / 2 ? -max : 0)); start.current = null; }}
        style={{ position: 'relative', background: 'var(--surface,#fff)', transform: dx ? `translateX(${dx}px)` : undefined, transition: start.current ? 'none' : 'transform .18s' }}
      >{children}</div>
    </div>
  );
};

export const MobilePatientList: React.FC<{
  title: string; search: string; searchPlaceholder: string; onSearch?: (v: string) => void;
  chips: { key: string; label: string }[]; chip: string; onChip: (k: string) => void;
  rows: MobileRowVM[]; onSelect: (id: number) => void; renderMenu?: (r: MobileRowVM) => React.ReactNode;
  swipeActions?: (r: MobileRowVM) => { label: string; onClick: () => void; bg: string }[];
}> = ({ title, search, searchPlaceholder, onSearch, chips, chip, onChip, rows, onSelect, renderMenu, swipeActions }) => {
  let letter = '';
  return (
    <>
      <div style={{ padding: '8px 18px 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ font: '500 30px/1.1 Fraunces,Georgia,serif', letterSpacing: '-.5px' }}>{title}</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 12px', borderRadius: 10, background: 'var(--surface,#fff)', border: '1px solid var(--line,#e5ded1)', color: 'var(--ink-faint,#98a39d)', fontSize: 14, margin: 0 }}>
          <input className="pp-search" value={search} onChange={e => onSearch?.(e.target.value)} placeholder={searchPlaceholder}
            style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', font: 'inherit', color: 'var(--ink,#1a2420)', padding: 0, height: 'auto' }} />
        </label>
        <div style={{ display: 'flex', gap: 6, overflow: 'hidden' }}>
          {chips.map(c => {
            const on = c.key === chip;
            return <span key={c.key} onClick={() => onChip(c.key)} style={{ flex: 'none', height: 30, padding: '0 12px', borderRadius: 99, background: on ? 'var(--accent,#0b3b2e)' : 'var(--surface,#fff)', color: on ? 'var(--accent-contrast,#f5efe2)' : undefined, border: on ? undefined : '1px solid var(--line,#e5ded1)', font: `500 12px/${on ? 30 : 28}px Inter,sans-serif`, cursor: 'pointer' }}>{c.label}</span>;
          })}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', background: 'var(--surface,#fff)', borderTop: '1px solid var(--line,#e5ded1)' }}>
        {rows.map(r => {
          const l = (r.name.trim()[0] || '#').toUpperCase();
          const head = l !== letter; letter = l;
          return (
            <React.Fragment key={r.id}>
              {head && <div style={{ padding: '6px 18px', font: `500 11px ${mono}`, color: 'var(--ink-faint,#98a39d)', background: 'var(--bg,#f7f4ee)' }}>{l}</div>}
              <SwipeRow actions={swipeActions ? swipeActions(r) : []}>
              <div onClick={() => onSelect(r.id)} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid var(--line-soft,#efeae0)', minHeight: 64, cursor: 'pointer' }}>
                <span style={{ width: 40, height: 40, borderRadius: '50%', background: '#e8d9bf', display: 'grid', placeItems: 'center', font: '600 13px Inter,sans-serif', color: '#3a3228', flex: 'none' }}>{r.initials}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 15, ...(r.alert ? { display: 'flex', gap: 6, alignItems: 'center' } : {}) }}>
                    {r.name}
                    {r.alert && <span style={{ width: 16, height: 16, borderRadius: 4, background: '#f3e3df', color: '#8b4b3d', display: 'grid', placeItems: 'center', font: '700 10px Inter,sans-serif' }}>!</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-muted,#5a6660)' }}>{r.sub}</div>
                </div>
                {r.badge && <span style={{ font: '500 11px Inter,sans-serif', padding: '3px 8px', borderRadius: 99, background: r.badge.bg, color: r.badge.color }}>{r.badge.text}</span>}
                {r.plain && <span style={{ font: '500 11px Inter,sans-serif', color: 'var(--ink-faint,#98a39d)' }}>{r.plain}</span>}
                {renderMenu?.(r)}
              </div>
              </SwipeRow>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};
